/**
 * SO-3b — the party's photos: the latest visit on the home card, all of them
 * on their own page, and the one they took themselves.
 *
 *   /site-office/:token/photos    the picker, the chosen visit, then every visit
 *
 * The office payload (GET /api/site-office-links/{token}) carries the visits,
 * newest first, already addressed through the party's own token door. The picker,
 * the notes block, the grid and the upload control live here and the home card
 * imports them, so a visit reads the same in both places.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { cacheBranding } from '@/utils/skin';
import OfficeStrip, { parsePreview } from '@/components/site-office/OfficeStrip';
import '@/styles/site-office.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const bare = axios.create();

const abs = (u) => (u && u.startsWith('/api') ? `${API}${u.replace(/^\/api/, '')}` : u);

export const dayLabel = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

/** One entry in a grid: a video plays, a lost picture says so, nothing broken is asked for. */
export const VisitShot = ({ photo, testId }) => {
  if (photo.unavailable || !photo.full_url) {
    return (
      <figure className="kso-shot">
        <span className="kso-ph kso-ph--gone" data-testid={testId}>Picture no longer available</span>
        {photo.caption ? <figcaption className="kso-cap"><span className="kso-ph__cap">{photo.caption}</span></figcaption> : null}
      </figure>
    );
  }
  return (
    <figure className="kso-shot">
      {photo.media_type === 'video' ? (
        <span className="kso-ph" data-testid={testId}>
          <video controls preload="metadata" playsInline
            poster={abs(photo.thumb_url) || undefined} src={abs(photo.full_url)} />
        </span>
      ) : (
        <a className="kso-ph" href={abs(photo.full_url)} target="_blank" rel="noreferrer" data-testid={testId}>
          <img src={abs(photo.thumb_url || photo.full_url)} alt={photo.caption || ''} loading="lazy" />
        </a>
      )}
      {(photo.caption || photo.category) ? (
        <figcaption className="kso-cap">
          {photo.caption ? <span className="kso-ph__cap">{photo.caption}</span> : null}
          {photo.category ? <span className="kso-chip">{photo.category}</span> : null}
        </figcaption>
      ) : null}
    </figure>
  );
};

export const VisitNotes = ({ notes, testId }) => {
  const rows = [
    ['Purpose', notes?.purpose], ['Observations', notes?.observations],
    ['Concerns', notes?.concerns], ['Next steps', notes?.next_steps],
  ].filter(([, text]) => text);
  if (!rows.length) return null;
  return (
    <div className="kso-visit__notes" data-testid={testId}>
      {rows.map(([label, text]) => <div key={label}><b>{label}</b>{text}</div>)}
    </div>
  );
};

export const VisitGrid = ({ visit, base }) => (
  <div className="kso-photos">
    {(visit.photos || []).map((photo, i) => (
      <VisitShot key={photo.full_url || `${visit.id}-${i}`} photo={photo}
        testId={`${base}-${visit.id}-photo-${i}`} />
    ))}
  </div>
);

/** A month grid with a dot on every day a visit happened. */
const MonthGrid = ({ visits, chosen, onPick }) => {
  const [month, setMonth] = useState(() => {
    const first = visits.find((v) => v.date);
    const d = first ? new Date(first.date) : new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const byDay = useMemo(() => {
    const map = {};
    visits.forEach((v) => { if (v.date) map[String(v.date).slice(0, 10)] = v; });
    return map;
  }, [visits]);
  const start = new Date(month.getFullYear(), month.getMonth(), 1);
  const days = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const cells = [...Array(start.getDay()).fill(null),
    ...Array.from({ length: days }, (_, i) => i + 1)];
  const key = (n) => `${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, '0')}-${String(n).padStart(2, '0')}`;
  return (
    <div data-testid="site-office-calendar">
      <div className="kso-visits">
        <span onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}
          data-testid="site-office-cal-prev">‹</span>
        <span className="cal">{month.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</span>
        <span onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}
          data-testid="site-office-cal-next">›</span>
      </div>
      <div className="kso-cal">
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => <span className="d" key={`h${i}`}>{d}</span>)}
        {cells.map((n, i) => {
          if (!n) return <span className="d" key={`b${i}`} />;
          const visit = byDay[key(n)];
          const on = visit && visit.id === chosen;
          return (
            <span key={key(n)}
              className={`d${visit ? ' has' : ''}${on ? ' on' : ''}`}
              onClick={visit ? () => onPick(visit.id) : undefined}
              data-testid={visit ? `site-office-cal-day-${key(n)}` : undefined}>
              {n}
            </span>
          );
        })}
      </div>
    </div>
  );
};

/** The chips, newest first, the first eight, then how many more, then the month. */
export const VisitPicker = ({ visits, chosen, onPick }) => {
  const [all, setAll] = useState(false);
  const [cal, setCal] = useState(false);
  const shown = all ? visits : visits.slice(0, 8);
  return (
    <>
      <div className="kso-visits" data-testid="site-office-visit-picker">
        {shown.map((v) => (
          <span key={v.id} className={v.id === chosen ? 'on' : undefined}
            onClick={() => onPick(v.id)} data-testid={`site-office-visit-chip-${v.id}`}>
            {dayLabel(v.date) || v.label}
          </span>
        ))}
        {!all && visits.length > shown.length ? (
          <span onClick={() => setAll(true)} data-testid="site-office-visit-more">
            +{visits.length - shown.length}
          </span>
        ) : null}
        <span className="cal" onClick={() => setCal((v) => !v)}
          data-testid="site-office-visit-cal">Calendar</span>
      </div>
      {cal ? <MonthGrid visits={visits} chosen={chosen} onPick={onPick} /> : null}
    </>
  );
};

/** The party's own progress photo: the phone's camera, a caption, a room. */
export const ProgressPhoto = ({ token, rooms = [], onDone, testId = 'site-office-progress', locked, lockedTitle }) => {
  const [open, setOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [caption, setCaption] = useState('');
  const [room, setRoom] = useState('');
  const [busy, setBusy] = useState(false);
  const input = useRef(null);

  const send = async () => {
    if (!files.length) { toast.error('Choose a photo first'); return; }
    setBusy(true);
    try {
      const form = new FormData();
      form.append('caption', caption);
      form.append('room', room);
      files.forEach((f) => form.append('files', f));
      const r = await bare.post(`${API}/site-office-links/${token}/photos`, form);
      toast.success(`Added to ${r.data.label}`);
      setFiles([]); setCaption(''); setRoom(''); setOpen(false);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'That did not send. Try again.');
    } finally { setBusy(false); }
  };

  return (
    <div>
      <button type="button" className="kso-btn brand" onClick={() => setOpen((v) => !v)}
        disabled={Boolean(locked)} title={lockedTitle}
        data-testid={`${testId}-open`}>Add a progress photo</button>
      {open ? (
        <div className="kso-punch__panel" data-testid={`${testId}-panel`}>
          <div className="kso-compose kso-compose--files">
            <input className="kso-in" placeholder="What this shows (optional)…" value={caption}
              onChange={(e) => setCaption(e.target.value)} data-testid={`${testId}-caption`} />
            <select className="kso-in" value={room} onChange={(e) => setRoom(e.target.value)}
              data-testid={`${testId}-room`}>
              <option value="">Room (optional)</option>
              {rooms.map((name) => <option key={name} value={name}>{name}</option>)}
            </select>
            <label className="kso-btn" data-testid={`${testId}-pick`}>
              {files.length ? `${files.length} ready` : 'Camera or files'}
              <input ref={input} type="file" accept="image/*,video/*" capture="environment"
                multiple hidden onChange={(e) => setFiles(Array.from(e.target.files || []).slice(0, 6))} />
            </label>
            <button type="button" className="kso-btn brand" disabled={busy || !files.length}
              onClick={send} data-testid={`${testId}-send`}>
              {busy ? 'Sending…' : 'Send'}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default function SiteOfficePhotos() {
  const { token } = useParams();
  const navigate = useNavigate();
  // SO-9a2 — the firm reading the photos from the preview.
  const [params] = useSearchParams();
  const preview = parsePreview(params.get('preview'));
  const [data, setData] = useState(null);
  const [gone, setGone] = useState('');
  const [chosen, setChosen] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await bare.get(`${API}/site-office-links/${token}`);
      setData(r.data);
      if (r.data?.branding) cacheBranding(r.data.branding);
      setChosen((prev) => prev || (r.data.photos || [])[0]?.id || '');
    } catch (_e) { setGone('This link is not valid'); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  if (gone) {
    return (
      <main className="kso-page" data-testid="site-office-photos">
        <div className="kso-wrap"><div className="kso-gate"><h1>{gone}</h1></div></div>
      </main>
    );
  }
  if (!data) {
    return (
      <main className="kso-page" data-testid="site-office-photos">
        <div className="kso-wrap"><div className="kso-gate"><p>Opening…</p></div></div>
      </main>
    );
  }

  const visits = data.photos || [];
  const shot = visits.find((v) => v.id === chosen) || visits[0];
  const total = visits.reduce((n, v) => n + (v.photo_count || 0), 0);
  const lost = visits.reduce((n, v) => n + (v.unavailable_count || 0), 0);
  const firm = data.firm_name || '';

  return (
    <main className="kso-page" data-testid="site-office-photos">
      <div className="kso-wrap">
        {preview ? (
          <OfficeStrip projectId={preview.projectId} membershipId={preview.membershipId}
            projectName={data.project?.name} partyName={data.party?.name} page="Photos" />
        ) : null}
        <header className="kso-head">
          {data.branding?.logo_url ? <img className="kso-head__logo" src={data.branding.logo_url} alt="" /> : null}
          <div className="kso-head__k"><b>{firm}</b> · Site Office</div>
          <div className="kso-head__who">
            <div className="n">{data.party?.name}</div>
            <div className="r">{data.project?.name}</div>
          </div>
        </header>
        <div className="kso-crumb" data-testid="site-office-photos-crumb">
          <button type="button" className="back"
            onClick={() => navigate(preview ? `/projects/${preview.projectId}/site-office/${preview.membershipId}?view=party` : `/site-office/${token}`)}
            data-testid="site-office-photos-back">‹ Site Office</button>
          <span>Site Office</span><span>›</span><b>Photos</b>
        </div>
        <div className="kso-grid">
          <section className="kso-card w12" data-testid="site-office-photos-chosen">
            <div className="kso-card__hd">
              Photos
              <span className="n" data-testid="site-office-photos-counts">
                {visits.length} visit{visits.length === 1 ? '' : 's'} · {total} photos
                {lost ? ` · ${lost} no longer available` : ''}
              </span>
            </div>
            <div className="kso-card__bd">
              <VisitPicker visits={visits} chosen={shot?.id} onPick={setChosen} />
              <ProgressPhoto token={token} rooms={data.rooms || []} onDone={load}
                locked={Boolean(preview)}
                lockedTitle={preview ? `Only ${data.party?.name} can do this` : undefined}
                testId="site-office-photos-add" />
              {shot ? (
                <>
                  <div className="kso-item__meta" data-testid="site-office-photos-chosen-label">{shot.label}</div>
                  <VisitNotes notes={shot.notes} testId={`site-office-photos-notes-${shot.id}`} />
                  <VisitGrid visit={shot} base="site-office-photos-chosen" />
                </>
              ) : <p className="kso-hint">No photos on this project yet.</p>}
            </div>
          </section>
          <section className="kso-card w12" data-testid="site-office-photos-all">
            <div className="kso-card__hd">All photos<span className="n">{visits.length} visits</span></div>
            <div className="kso-card__bd">
              {visits.map((v) => (
                <div className="kso-visit" key={v.id} data-testid={`site-office-photos-visit-${v.id}`}>
                  <div className="kso-item__meta">{v.label} · {v.photo_count} photos
                    {v.unavailable_count ? ` · ${v.unavailable_count} no longer available` : ''}</div>
                  <VisitNotes notes={v.notes} testId={`site-office-photos-all-notes-${v.id}`} />
                  <VisitGrid visit={v} base="site-office-photos-all" />
                </div>
              ))}
            </div>
          </section>
        </div>
        <div className="kso-foot">{firm} · this link is personal to {data.party?.name}</div>
      </div>
    </main>
  );
}
